import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["availableList", "currentList", "dropZone"]
  static values = {
    teamId: Number,
    addUrl: String,
    removeUrl: String
  }

  connect() {}

  dragStart(event) {
    const memberCard = event.currentTarget
    const userId = memberCard.dataset.userId
    const userName = memberCard.dataset.userName
    const membershipId = memberCard.dataset.membershipId
    const isCurrentMember = memberCard.closest("[data-team-member-drag-target='currentList']") !== null

    event.dataTransfer.effectAllowed = isCurrentMember ? "move" : "copy"
    event.dataTransfer.setData("text/plain", userId)
    event.dataTransfer.setData("application/json", JSON.stringify({
      userId: userId,
      userName: userName,
      membershipId: membershipId || null,
      isCurrentMember: isCurrentMember
    }))

    memberCard.classList.add("opacity-50", "cursor-grabbing")
  }

  dragEnd(event) {
    event.currentTarget.classList.remove("opacity-50", "cursor-grabbing")
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const dropZone = event.currentTarget
    dropZone.classList.add("border-primary", "bg-primary-50")
  }

  dragLeave(event) {
    const dropZone = event.currentTarget
    dropZone.classList.remove("border-primary", "bg-primary-50")
  }

  async drop(event) {
    event.preventDefault()
    event.stopPropagation()

    // Remove visual feedback from all drop zones
    this.dropZoneTargets.forEach(zone => {
      zone.classList.remove("border-primary", "bg-primary-50")
    })
    this.currentListTarget?.classList.remove("border-primary", "bg-primary-50")
    this.availableListTarget?.classList.remove("border-primary", "bg-primary-50")

    // Get the drag data
    let data
    try {
      const jsonData = event.dataTransfer.getData("application/json")
      if (!jsonData) {
        console.error("No drag data found")
        return
      }
      data = JSON.parse(jsonData)
    } catch (error) {
      console.error("Error parsing drag data:", error)
      return
    }

    // Find which list we're dropping into by checking the element and its parents
    const element = event.currentTarget
    let targetList = null
    let isCurrentList = false
    let isAvailableList = false

    // Check if the element itself is a list target
    if (element === this.currentListTarget || element.getAttribute("data-team-member-drag-target") === "currentList") {
      targetList = this.currentListTarget
      isCurrentList = true
    } else if (element === this.availableListTarget || element.getAttribute("data-team-member-drag-target") === "availableList") {
      targetList = this.availableListTarget
      isAvailableList = true
    } else {
      // Check parent elements
      const currentListParent = element.closest("[data-team-member-drag-target='currentList']")
      const availableListParent = element.closest("[data-team-member-drag-target='availableList']")

      if (currentListParent) {
        targetList = this.currentListTarget
        isCurrentList = true
      } else if (availableListParent) {
        targetList = this.availableListTarget
        isAvailableList = true
      }
    }

    if (!targetList) {
      console.error("Could not find target list")
      return
    }

    if (isCurrentList && !data.isCurrentMember) {
      await this.addMember(data.userId, data.userName, event)
    } else if (isAvailableList && data.isCurrentMember) {
      await this.removeMember(data.userId, data.userName, data.membershipId, event)
    }
  }

  async addMember(userId, userName, event) {
    const formData = new FormData()
    formData.append("user_id", userId)
    formData.append("role", "member")

    try {
      const response = await fetch(this.addUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      if (response.ok) {
        // Reload the page to show updated lists
        window.location.reload()
      } else {
        const errorText = await response.text()
        console.error("Error adding member")
        alert(`Error adding ${userName} to the team: ${errorText}`)
      }
    } catch (error) {
      console.error("Error adding member:", error)
      alert(`Error adding ${userName} to the team: ${error.message}`)
    }
  }

  async removeMember(userId, userName, membershipId, event) {
    if (!membershipId) {
      console.error("Could not find membership ID for user")
      alert(`Could not find membership information for ${userName}. Please use the Remove button instead.`)
      return
    }

    if (!confirm(`Are you sure you want to remove ${userName} from this team?`)) {
      return
    }

    // Construct the correct URL - removeUrlValue is the collection path, we need to append the ID
    const deleteUrl = `${this.removeUrlValue}/${membershipId}`

    try {
      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        // Reload the page to show updated lists
        window.location.reload()
      } else {
        const errorText = await response.text()
        console.error("Error removing member")
        alert(`Error removing ${userName} from the team: ${errorText}`)
      }
    } catch (error) {
      console.error("Error removing member:", error)
      alert(`Error removing ${userName} from the team: ${error.message}`)
    }
  }
}
